import type { PortSummary, ShipNow, TrailPoint } from "@cruise/core";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { compact, degrees, knots, latLon, minutesAgo, stateLabel } from "../format";
import { accentFor, palette, withAlpha } from "../palette";
import { landFor, onLandLoaded } from "./land";
import landDots from "./landDots.json";

/**
 * Map colours come from palette.ts. The sea is a clear blue so the near-black
 * panels stand out against it; land is a lighter slate than the sea; ships
 * stay brightest.
 */
const { map: MAP, ship: SHIP } = palette;
const SEA_GLOW = withAlpha(MAP.seaGlow, 0.5);
const SEA_GLOW_EDGE = withAlpha(MAP.sea, 0);
const COAST = withAlpha(MAP.coastCruise, 0.75);
const COAST_OPS = withAlpha(MAP.coastOps, 0.6);
const GRID = withAlpha(MAP.grid, 0.07);
const GRID_OPS = withAlpha(MAP.grid, 0.11);
const PORT_RING = withAlpha(MAP.port, 0.35);
const PORT_LABEL = withAlpha(MAP.port, 0.85);
const SHIP_HALO = withAlpha(SHIP.halo, 0.08);
const SHIP_LABEL = withAlpha(SHIP.label, 0.9);
const NO_SIGNAL_RING = withAlpha(SHIP.noRecentSignal, 0.6);
const NO_SIGNAL_LABEL = withAlpha(SHIP.noRecentSignal, 0.85);
const COURSE_LINE = withAlpha(SHIP.moving, 0.5);
const FLEET_TRAIL = withAlpha(SHIP.trail, 0.4);
/** Map label fonts. */
const LABEL_FONT = "ui-monospace,Menlo,monospace";
const PORT_LABEL_PX = 10;
/** Below this zoom only the selected port is labelled. */
const PORT_LABEL_MIN_ZOOM = 3;
const SHIP_LABEL_PX = 10;
/** The "LAST AIS …" line under a ship that has gone quiet. */
const SHIP_NOTE_PX = 9;
/**
 * Space between a hovered point and its tooltip. Ships: clears the largest
 * halo (selected ship, 14 px) and the second label line below the dot.
 */
const SHIP_TIP_GAP = 20;
/** Closest a tooltip comes to the window edge. */
const TIP_MARGIN = 8;
/** Zoom by which the mockup's dot texture has faded out completely. */
const DOTS_GONE_ZOOM = 3;
/** Longitude shifts at which each land ring is drawn (for rings past ±180°). */
const LON_SHIFTS = [0, -360, 360] as const;

export type MapMode = "cruise" | "ops";

export interface CanvasMapProps {
  mode: MapMode;
  ships: ShipNow[];
  ports: PortSummary[];
  selectedImo: string | null;
  selectedPort: string | null;
  /** The followed ship's track, oldest first. */
  track: readonly TrailPoint[];
  /** Trails of all ships by IMO (drawn for moving ships only), or null when all trails are off. */
  trails: ReadonlyMap<string, readonly TrailPoint[]> | null;
  onSelectShip: (imo: string) => void;
  onSelectPort: (locode: string) => void;
}

/** Map centre (degrees) and zoom (1 = 360° of longitude across the canvas width). */
interface View {
  lon: number;
  lat: number;
  zoom: number;
}

interface Tip {
  /** Screen position of the hovered ship or port (not the cursor). */
  x: number;
  y: number;
  /** Space kept clear between the point and the tooltip. */
  gap: number;
  title: string;
  lines: string[];
}

const INITIAL_VIEW: View = { lon: -35, lat: 30, zoom: 1.7 };
/** Below 1 the whole world is narrower than the screen; needed to fit a fleet spread across oceans. */
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 80;
const HIT_PX = 10;
const DRAG_THRESHOLD_PX = 4;
/** Zoom change per pixel of wheel movement: one mouse-wheel notch (~100 px) ≈ 11%. */
const WHEEL_ZOOM_PER_PX = 0.0012;
/** Largest wheel movement counted in one event, so a single flick cannot jump far. */
const WHEEL_MAX_STEP_PX = 100;
/** Degrees of margin around the ships when fitting the first view. */
const FIT_PADDING_DEG = 6;

// Land dots from the mockup: a 2400×1200 equirectangular grid → lon/lat pairs.
const LAND = (() => {
  const out = new Float32Array(landDots.length * 2);
  landDots.forEach((p, i) => {
    out[i * 2] = ((p[0] ?? 0) / 2400) * 360 - 180;
    out[i * 2 + 1] = 90 - ((p[1] ?? 0) / 1200) * 180;
  });
  return out;
})();

function project(view: View, w: number, h: number) {
  const k = (w / 360) * view.zoom; // pixels per degree
  return {
    k,
    toScreen: (lon: number, lat: number): [number, number] => [(lon - view.lon) * k + w / 2, (view.lat - lat) * k + h / 2],
    toLonLat: (x: number, y: number): [number, number] => [(x - w / 2) / k + view.lon, view.lat - (y - h / 2) / k],
  };
}

/**
 * The part of the screen not covered by the top bar, side panels and bottom
 * strip (matches the layout in theme.css; the right column hides below 1100 px).
 */
function openArea(w: number, h: number) {
  const wide = w > 1100;
  return {
    left: w > 760 ? 362 : 16,
    right: wide ? w - 342 : w - 16,
    top: 110,
    bottom: h - (w > 760 ? 100 : h * 0.5),
  };
}

function clampZoom(zoom: number): number {
  return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom));
}

/** A view that puts every positioned ship inside the open area. */
function fitView(ships: ShipNow[], w: number, h: number): View | null {
  const positions = ships.flatMap((s) => (s.position ? [s.position] : []));
  if (positions.length === 0) return null;
  const lons = positions.map((p) => p.longitude);
  const lats = positions.map((p) => p.latitude);
  const minLon = Math.min(...lons) - FIT_PADDING_DEG;
  const maxLon = Math.max(...lons) + FIT_PADDING_DEG;
  const minLat = Math.min(...lats) - FIT_PADDING_DEG;
  const maxLat = Math.max(...lats) + FIT_PADDING_DEG;
  const area = openArea(w, h);
  const k = Math.min((area.right - area.left) / Math.max(maxLon - minLon, 10), (area.bottom - area.top) / Math.max(maxLat - minLat, 6));
  const zoom = clampZoom((k * 360) / w);
  const kk = (w / 360) * zoom;
  return centreOn((minLon + maxLon) / 2, (minLat + maxLat) / 2, zoom, w, h, kk);
}

/** A view at `zoom` with (lon, lat) at the centre of the open area. */
function centreOn(lon: number, lat: number, zoom: number, w: number, h: number, k = (w / 360) * zoom): View {
  const area = openArea(w, h);
  return {
    zoom,
    lon: lon - ((area.left + area.right) / 2 - w / 2) / k,
    lat: Math.max(-80, Math.min(80, lat + ((area.top + area.bottom) / 2 - h / 2) / k)),
  };
}

function portRadius(port: PortSummary): number {
  return port.hasSchedule ? Math.min(26, 5 + Math.sqrt(port.today.knownPassengerCapacity) / 10) : 4;
}

/**
 * Adds a ship's trail to the current path. Points are projected from latitude
 * and longitude on every frame, so the line keeps its true position and length
 * at any zoom. The trail stops at the time of the ship's dot and ends exactly on
 * it, so the line and the dot always meet. The pen lifts where the trail
 * crosses the 180° meridian.
 */
function traceTrail(
  ctx: CanvasRenderingContext2D,
  points: readonly TrailPoint[],
  ship: ShipNow | undefined,
  toScreen: (lon: number, lat: number) => [number, number],
) {
  const pos = ship?.position ?? null;
  const endMs = pos ? Date.parse(pos.receivedAt) : Infinity;
  let prevLon: number | null = null;
  let lastMs = -Infinity;
  const to = (lat: number, lon: number) => {
    const [x, y] = toScreen(lon, lat);
    if (prevLon === null || Math.abs(lon - prevLon) > 180) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
    prevLon = lon;
  };
  for (const [lat, lon, ms] of points) {
    if (ms > endMs) break;
    to(lat, lon);
    lastMs = ms;
  }
  if (pos && lastMs < endMs && prevLon !== null) to(pos.latitude, pos.longitude);
}

function draw(ctx: CanvasRenderingContext2D, w: number, h: number, view: View, p: CanvasMapProps, hover: string | null) {
  const { k, toScreen } = project(view, w, h);
  const ops = p.mode === "ops";
  const accent = accentFor(p.mode);

  ctx.fillStyle = MAP.sea;
  ctx.fillRect(0, 0, w, h);
  const glow = ctx.createRadialGradient(w * 0.5, h * 0.45, h * 0.1, w * 0.5, h * 0.5, h * 0.95);
  glow.addColorStop(0, SEA_GLOW);
  glow.addColorStop(1, SEA_GLOW_EDGE);
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, w, h);

  // Graticule: finer when zoomed in, denser in ops mode.
  const step = view.zoom >= 12 ? 5 : view.zoom >= 4 ? 10 : ops ? 15 : 30;
  ctx.strokeStyle = ops ? GRID_OPS : GRID;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let lon = -180; lon <= 180; lon += step) {
    const [x] = toScreen(lon, 0);
    if (x >= 0 && x <= w) {
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
    }
  }
  for (let lat = -90; lat <= 90; lat += step) {
    const [, y] = toScreen(0, lat);
    if (y >= 0 && y <= h) {
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
    }
  }
  ctx.stroke();

  // Land: filled Natural Earth outlines with a coastline (1:50m from DETAIL_ZOOM).
  const land = landFor(view.zoom);
  if (land) {
    const west = view.lon - w / 2 / k;
    const east = view.lon + w / 2 / k;
    const north = view.lat + h / 2 / k;
    const south = view.lat - h / 2 / k;
    ctx.beginPath();
    for (let r = 0; r < land.rings.length; r++) {
      const b = r * 4;
      if ((land.boxes[b + 3] ?? 0) < south || (land.boxes[b + 1] ?? 0) > north) continue;
      const ring = land.rings[r]!;
      // Rings near the 180° meridian extend past ±180 (see land.ts): draw the
      // copy shifted by 360° too when that copy is in view.
      for (const shift of LON_SHIFTS) {
        if ((land.boxes[b + 2] ?? 0) + shift < west || (land.boxes[b] ?? 0) + shift > east) continue;
        for (let i = 0; i < ring.length; i += 2) {
          const x = ((ring[i] ?? 0) + shift - view.lon) * k + w / 2;
          const y = (view.lat - (ring[i + 1] ?? 0)) * k + h / 2;
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.closePath();
      }
    }
    ctx.fillStyle = MAP.land;
    ctx.fill("evenodd"); // lakes are holes in the land polygons
    ctx.strokeStyle = ops ? COAST_OPS : COAST;
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  // The mockup's dot texture: full strength at the world view, gone by 3× zoom
  // (the dots are ~2.4° apart, so zoomed in they only add noise). Stronger
  // while the outlines are still loading.
  const dotAlpha = land ? Math.max(0, Math.min(1, (DOTS_GONE_ZOOM - view.zoom) / 1.5)) * 0.5 : 0.7;
  if (dotAlpha > 0.01) {
    const size = 1.6;
    ctx.fillStyle = withAlpha(MAP.landDots, Number(dotAlpha.toFixed(3)));
    for (let i = 0; i < LAND.length; i += 2) {
      const x = ((LAND[i] ?? 0) - view.lon) * k + w / 2;
      const y = (view.lat - (LAND[i + 1] ?? 0)) * k + h / 2;
      if (x < -size || y < -size || x > w + size || y > h + size) continue;
      ctx.fillRect(x - size / 2, y - size / 2, size, size);
    }
  }

  // Ports: ring size follows known passenger capacity scheduled today.
  ctx.font = `${PORT_LABEL_PX}px ${LABEL_FONT}`;
  for (const port of p.ports) {
    const [x, y] = toScreen(port.longitude, port.latitude);
    const r = portRadius(port);
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    if (port.hasSchedule) {
      ctx.fillStyle = withAlpha(accent, ops ? 0.12 : 0.1);
      ctx.fill();
    }
    ctx.strokeStyle = port.locode === p.selectedPort ? accent : PORT_RING;
    ctx.lineWidth = port.locode === p.selectedPort ? 1.5 : 1;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(x, y, 2.2, 0, Math.PI * 2);
    ctx.fillStyle = accent;
    ctx.fill();
    // Port names only when zoomed in (or selected): dozens of European ports overlap at world scale.
    if (view.zoom < PORT_LABEL_MIN_ZOOM && port.locode !== p.selectedPort) continue;
    ctx.fillStyle = PORT_LABEL;
    const label = ops && port.hasSchedule ? `${port.name.toUpperCase()} · ${port.today.shipsScheduled} due` : port.name;
    ctx.fillText(label, x + r + 4, y - 4);
  }

  // All trails on: the last 2 hours of every moving ship except the followed one.
  if (p.trails) {
    ctx.beginPath();
    for (const ship of p.ships) {
      if (ship.state !== "under-way" || ship.imo === p.selectedImo) continue;
      const trail = p.trails.get(ship.imo);
      if (trail) traceTrail(ctx, trail, ship, toScreen);
    }
    ctx.strokeStyle = FLEET_TRAIL;
    ctx.lineWidth = 1.2;
    ctx.stroke();
  }

  // Followed ship's 24-hour track.
  if (p.track.length > 0) {
    ctx.beginPath();
    traceTrail(ctx, p.track, p.ships.find((s) => s.imo === p.selectedImo), toScreen);
    ctx.strokeStyle = withAlpha(accent, 0.55);
    ctx.lineWidth = 1.4;
    ctx.stroke();
  }

  for (const ship of p.ships) {
    const pos = ship.position;
    if (!pos) continue; // never seen: nothing to draw
    const [x, y] = toScreen(pos.longitude, pos.latitude);
    if (x < -20 || y < -20 || x > w + 20 || y > h + 20) continue;
    const selected = ship.imo === p.selectedImo;
    const stale = ship.state === "no-recent-signal";
    const r = selected ? 5.5 : ops ? 2.8 : 3.4;

    // Course line for moving ships, longer when faster.
    if (ship.state === "under-way" && pos.cog !== null) {
      const rad = ((pos.cog - 90) * Math.PI) / 180;
      const len = 8 + (pos.sog ?? 0) * 0.8;
      ctx.setLineDash([3, 4]);
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + Math.cos(rad) * len, y + Math.sin(rad) * len);
      ctx.strokeStyle = COURSE_LINE;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.setLineDash([]);
    }

    const dot = ship.state === "stopped" ? SHIP.stopped : SHIP.moving;
    ctx.beginPath();
    ctx.arc(x, y, r * 2.6, 0, Math.PI * 2);
    ctx.fillStyle = selected && !stale ? withAlpha(dot, 0.18) : SHIP_HALO;
    ctx.fill();

    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    if (stale) {
      // No AIS for over an hour: hollow and dim — position is where it was last heard.
      ctx.strokeStyle = NO_SIGNAL_RING;
      ctx.lineWidth = 1.2;
      ctx.stroke();
    } else {
      ctx.fillStyle = dot;
      ctx.fill();
    }

    if (selected || ship.imo === hover || (ops && view.zoom >= 4)) {
      ctx.font = `${selected ? "600 " : ""}${SHIP_LABEL_PX}px ${LABEL_FONT}`;
      ctx.fillStyle = selected ? SHIP.labelSelected : SHIP_LABEL;
      ctx.fillText(selected ? ship.name.toUpperCase() : ship.name, x + r + 5, y + 3);
      if (stale) {
        ctx.font = `${SHIP_NOTE_PX}px ${LABEL_FONT}`;
        ctx.fillStyle = NO_SIGNAL_LABEL;
        ctx.fillText(`LAST AIS ${minutesAgo(ship.minutesSinceLastPosition).toUpperCase()}`, x + r + 5, y + 14);
      }
    }
  }
}

export function CanvasMap(props: CanvasMapProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const propsRef = useRef(props);
  const viewRef = useRef<View>(INITIAL_VIEW);
  const hoverRef = useRef<string | null>(null);
  const frameRef = useRef(0);
  /** Set once the view was fitted to the ships or the user moved the map. */
  const settledRef = useRef(false);
  const [tip, setTip] = useState<Tip | null>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const [cursor, setCursor] = useState<string>("--.--°N --.--°E");
  const [dragging, setDragging] = useState(false);

  propsRef.current = props;

  const requestDraw = () => {
    if (frameRef.current) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = 0;
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (!canvas || !ctx) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = window.innerWidth;
      const h = window.innerHeight;
      if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      draw(ctx, w, h, viewRef.current, propsRef.current, hoverRef.current);
    });
  };

  useEffect(requestDraw);

  // Place the tooltip once its size is known, before the browser paints it:
  // centred above the point with `gap` clear space, or below it when there is
  // no room above. The point, its halo and its label stay visible.
  useLayoutEffect(() => {
    const el = tipRef.current;
    if (!tip || !el) return;
    const { width, height } = el.getBoundingClientRect();
    const left = Math.max(TIP_MARGIN, Math.min(tip.x - width / 2, window.innerWidth - width - TIP_MARGIN));
    const above = tip.y - tip.gap - height;
    el.style.left = `${left}px`;
    el.style.top = `${above >= TIP_MARGIN ? above : tip.y + tip.gap}px`;
  }, [tip]);

  // Outlines arrive after the first frame (and again when zooming past DETAIL_ZOOM).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => onLandLoaded(requestDraw), []);

  // First load: fit the view to where the ships are, unless the map was already moved.
  useEffect(() => {
    if (settledRef.current) return;
    const view = fitView(props.ships, window.innerWidth, window.innerHeight);
    if (!view) return;
    settledRef.current = true;
    viewRef.current = view;
    requestDraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.ships]);

  // A ship picked from a panel may be behind a panel or off screen: bring it into view.
  useEffect(() => {
    const ship = propsRef.current.ships.find((s) => s.imo === props.selectedImo);
    if (!ship?.position) return;
    const w = window.innerWidth;
    const h = window.innerHeight;
    const [x, y] = project(viewRef.current, w, h).toScreen(ship.position.longitude, ship.position.latitude);
    const area = openArea(w, h);
    if (x >= area.left && x <= area.right && y >= area.top && y <= area.bottom) return;
    viewRef.current = centreOn(ship.position.longitude, ship.position.latitude, viewRef.current.zoom, w, h);
    requestDraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedImo]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let down: { x: number; y: number; moved: boolean } | null = null;

    const hitTest = (x: number, y: number): { ship?: ShipNow; port?: PortSummary } => {
      const { toScreen } = project(viewRef.current, window.innerWidth, window.innerHeight);
      let best: { d: number; ship?: ShipNow; port?: PortSummary } = { d: Infinity };
      for (const ship of propsRef.current.ships) {
        if (!ship.position) continue;
        const [sx, sy] = toScreen(ship.position.longitude, ship.position.latitude);
        const d = Math.hypot(sx - x, sy - y);
        if (d < HIT_PX && d < best.d) best = { d, ship };
      }
      if (best.ship) return { ship: best.ship };
      for (const port of propsRef.current.ports) {
        const [px, py] = toScreen(port.longitude, port.latitude);
        const d = Math.hypot(px - x, py - y);
        if (d < Math.max(HIT_PX, portRadius(port)) && d < best.d) best = { d, port };
      }
      return best.port ? { port: best.port } : {};
    };

    const onPointerDown = (e: PointerEvent) => {
      down = { x: e.clientX, y: e.clientY, moved: false };
      canvas.setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e: PointerEvent) => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const view = viewRef.current;
      if (down) {
        const dx = e.clientX - down.x;
        const dy = e.clientY - down.y;
        if (!down.moved && Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) {
          down.moved = true;
          settledRef.current = true;
          setDragging(true);
          setTip(null);
        }
        if (down.moved) {
          const { k } = project(view, w, h);
          viewRef.current = {
            ...view,
            lon: view.lon - dx / k,
            lat: Math.max(-80, Math.min(80, view.lat + dy / k)),
          };
          down.x = e.clientX;
          down.y = e.clientY;
          requestDraw();
          return;
        }
      }

      const projection = project(view, w, h);
      const [lon, lat] = projection.toLonLat(e.clientX, e.clientY);
      setCursor(latLon(lat, lon));
      const hit = hitTest(e.clientX, e.clientY);
      const nextHover = hit.ship?.imo ?? null;
      if (nextHover !== hoverRef.current) {
        hoverRef.current = nextHover;
        requestDraw();
      }
      canvas.classList.toggle("pointing", Boolean(hit.ship || hit.port));
      if (hit.ship) {
        const s = hit.ship;
        const pos = s.position!;
        const [x, y] = projection.toScreen(pos.longitude, pos.latitude);
        setTip({
          x,
          y,
          gap: SHIP_TIP_GAP,
          title: s.name,
          lines: [
            `${s.line}`,
            `${stateLabel(s.state)} · last AIS ${minutesAgo(s.minutesSinceLastPosition)}`,
            s.state === "under-way" ? `${knots(pos.sog)} · course ${degrees(pos.cog)}` : s.nearPort ? `near ${s.nearPort.name}` : latLon(pos.latitude, pos.longitude),
          ],
        });
      } else if (hit.port) {
        const port = hit.port;
        const [x, y] = projection.toScreen(port.longitude, port.latitude);
        setTip({
          x,
          y,
          // Clears the port ring and its name, which sits above and to the right.
          gap: Math.max(portRadius(port), 10) + 12,
          title: port.name,
          lines: port.hasSchedule
            ? [
                `${port.today.shipsScheduled} ship(s) scheduled today`,
                `known capacity ${compact(port.today.knownPassengerCapacity)} passengers`,
              ]
            : ["schedule not readable yet"],
        });
      } else {
        setTip(null);
      }
    };

    const onPointerUp = (e: PointerEvent) => {
      if (down && !down.moved) {
        const hit = hitTest(e.clientX, e.clientY);
        if (hit.ship) propsRef.current.onSelectShip(hit.ship.imo);
        else if (hit.port) propsRef.current.onSelectPort(hit.port.locode);
      }
      down = null;
      setDragging(false);
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      settledRef.current = true;
      const w = window.innerWidth;
      const h = window.innerHeight;
      const view = viewRef.current;
      const [lon, lat] = project(view, w, h).toLonLat(e.clientX, e.clientY);
      // Zoom in proportion to how far the wheel moved: trackpads and smooth
      // wheels send many small events, so a fixed step per event raced.
      const pixels =
        e.deltaMode === WheelEvent.DOM_DELTA_LINE ? e.deltaY * 16 : e.deltaMode === WheelEvent.DOM_DELTA_PAGE ? e.deltaY * h : e.deltaY;
      const step = Math.max(-WHEEL_MAX_STEP_PX, Math.min(WHEEL_MAX_STEP_PX, pixels));
      const zoom = clampZoom(view.zoom * Math.exp(-step * WHEEL_ZOOM_PER_PX));
      const k = (w / 360) * zoom;
      // Keep the point under the cursor fixed while zooming.
      viewRef.current = {
        zoom,
        lon: lon - (e.clientX - w / 2) / k,
        lat: Math.max(-80, Math.min(80, lat + (e.clientY - h / 2) / k)),
      };
      requestDraw();
    };

    const onLeave = () => {
      setTip(null);
      hoverRef.current = null;
      requestDraw();
    };

    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerup", onPointerUp);
    canvas.addEventListener("pointerleave", onLeave);
    canvas.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("resize", requestDraw);
    return () => {
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointerleave", onLeave);
      canvas.removeEventListener("wheel", onWheel);
      window.removeEventListener("resize", requestDraw);
      cancelAnimationFrame(frameRef.current);
      frameRef.current = 0;
    };
    // requestDraw only reads refs; the listeners are attached once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <canvas id="sea" ref={canvasRef} className={dragging ? "dragging" : undefined} />
      {tip && (
        <div id="tip" ref={tipRef} className="glass">
          <span className="tn">{tip.title}</span>
          {tip.lines.map((line) => (
            <div key={line}>{line}</div>
          ))}
        </div>
      )}
      <div id="readout" className="glass">
        <span>{cursor}</span>
        <span>ZOOM {viewRef.current.zoom.toFixed(1)}×</span>
        <span>DRAG TO PAN · SCROLL TO ZOOM</span>
      </div>
    </>
  );
}
